// THE eye pipeline: assets in, an imperative handle out, one path for both mounts.

import { latestOnly } from '../../lib/latestOnly.js'
import { getOgsComputeDevice } from '../inference/gpuDevice.ts'
import { createEyeController } from './controller/index.ts'
import type { EyeController } from './controller/index.ts'
import { eyeShellsFromMesh } from './headEyeMesh.ts'
import type { EyeAssets } from './eyeAssets.ts'
import { resolveCondOwners } from './eyeCond.ts'
import { createEyeDecoders } from './eyeDecoders.ts'
import { createEyePass, cameraView } from './eyePass.ts'
import type { CameraLike, CameraView } from './eyePass.ts'
import { MAX_EYE_LIGHTS } from './eyeLiftParams.ts'
import { EMPTY_LIGHT_SET, resolveEyeLights } from './eyeSceneLights.ts'
import type { EyeLightSet, EyeLightSource } from './eyeSceneLights.ts'
import { createEyeSplatMesh } from './eyeSplatMesh.ts'
import { GpuEyeLifter } from './GpuEyeLifter.ts'
import type { EyeSide } from './eyeSide.ts'
import type { Vector3 } from '../math/index.ts'
// One definition of "the camera moved enough to re-render", shared with the head/multi-region
// recolor — see the header there for why nobody subscribes to __ORBIT_CONTROLS__ alone.
import { viewMoved } from '../cameraWatch.ts'

// eye_lift.wgsl's own binding count; the head lift's 9 would refuse adapters reporting 8.
const EYE_LIFT_STORAGE_BUFFERS = 5

export type SceneLike = { add(object: any): void, remove?(object: any): void, children?: any[] }

export type EyeSystemOptions = {
  scene: SceneLike
  camera: CameraLike
  assets: EyeAssets
  /** Defaults to one built from the assets; inject when something outside owns gaze. */
  controller?: EyeController
  /** Default true: eye contact is what makes the gaze look alive. */
  lookAtCamera?: boolean
  /** False parks the pass — a character switch or a torn-down renderer chain. */
  isActive?: () => boolean
  /** Run on dispose, after the system's own teardown. */
  onDispose?: () => void
}

export type EyeSystem = Awaited<ReturnType<typeof createEyeSystem>>

export async function createEyeSystem(options: EyeSystemOptions) {
  const { scene, camera, assets } = options
  const { charts, mesh, meta, models } = assets
  const isActive = options.isActive ?? (() => true)

  const controller: EyeController = options.controller ?? createEyeController(eyeShellsFromMesh(mesh))
  const condOwners = resolveCondOwners(
    { left: mesh.eyes.left.axisOptical, right: mesh.eyes.right.axisOptical },
    meta.headPose, meta.gazeReference)

  const splatMesh = createEyeSplatMesh(charts.left.numTexels + charts.right.numTexels)
  scene.add(splatMesh.mesh)

  const decoders = await createEyeDecoders(models, meta.uvResolution)
  const lifter = await buildLifter(assets, splatMesh.capacity)
  const pass = createEyePass({ decoders, lifter, splatMesh, meta, condOwners })

  const { owners, alignment } = condOwners
  console.info(`[eyes] ${splatMesh.count} splats — chart ${charts.left.numTexels}`
    + `+${charts.right.numTexels} @ ${meta.chartResolution}², decoders on '${decoders.provider}', `
    + `cond owners left<-${owners.left} right<-${owners.right} `
    + `(alignment ${alignment.left?.toFixed(4)}/${alignment.right?.toFixed(4)})`)

  let followCamera = options.lookAtCamera !== false
  let specGain = 1
  let lights: EyeLightSet = EMPTY_LIGHT_SET
  let lightSource: EyeLightSource = 'none'
  /** The view the last completed pass ran at — what syncCamera compares against. */
  let passView: CameraView | null = null
  let enabled = true
  let visible = true
  let disposed = false
  let lastError: string | null = null

  const applyVisibility = () => { splatMesh.mesh.visible = visible && enabled }

  // One pass, re-reading the lights so a light you move moves the glint.
  const relift = latestOnly(async () => {
    if (disposed || !enabled || !isActive()) return
    const view = cameraView(camera)
    if (followCamera) controller.gaze.lookAtPoint(view.origin)
    const poses = controller.update()
    const resolved = resolveEyeLights(scene, controller.midpoint(), meta.spec, MAX_EYE_LIGHTS)
    lights = resolved
    lightSource = resolved.source
    lifter.setLights(lights)
    try {
      await pass.run(poses, view)
      passView = view
      lastError = null
    } catch (error) {
      lastError = (error as any)?.message ?? String(error)
      console.error('[eyes] lift pass failed', error)
    }
  })

  await relift()
  // After the first pass: two of the three are only known once appr has run.
  console.info(`[eyes] first pass: lights ${lights.count} (${lightSource}), appr channels `
    + `${pass.colorChannels}, eye contact ${followCamera ? 'on' : 'off'}`)

  return {
    /** Everything that moves the eyes. `system.controller.gaze` is the gaze policy. */
    controller,
    mesh,
    splatMesh,

    /** Eye contact on/off. Off returns the eyes to their own optical axes. */
    setLookAtCamera(follow: boolean) {
      followCamera = !!follow
      if (!followCamera) controller.gaze.rest()
      void relift()
    },

    /** Manual gaze: a world direction, shared or per eye. Turns eye contact off. */
    setGaze(target: Vector3 | Record<EyeSide, Vector3>) {
      followCamera = false
      controller.gaze.lookAlong(target)
      void relift()
    },

    setVisible(next: boolean) { visible = !!next; applyVisibility() },

    setEnabled(next: boolean) {
      enabled = !!next
      applyVisibility()
      if (enabled) void relift()
    },

    /** The offline renderer's --eye_spec_gain: 0 renders the diffuse alone. */
    setSpecGain(gain: number) {
      specGain = Number.isFinite(gain) ? gain : 1
      lifter.setSpecGain(specGain)
      void relift()
    },

    /** Run a pass now. Coalesced: a burst costs one extra pass, with the newest view. */
    relift: () => relift(),

    /** Re-lift if the CAMERA moved; call per frame. The watch that cannot go stale — docs/OGS_EYES.md. */
    /** THE camera watch. Call it every frame; it returns without work unless the
     *  view moved past the pass's own epsilon.
     *
     *  There was a second one — a `controls` 'change' subscription that relifted
     *  unconditionally — and it was removed rather than kept as a cheap path,
     *  because it could not be relied on and did not need to be. `mountEyeBranch`
     *  read `window.__ORBIT_CONTROLS__` AT MOUNT, which is the case OGSModel's own
     *  useFrame comment describes as "may not exist when the branch mounts", so
     *  the subscription was frequently never made; when it was, that global's
     *  identity SWAPS on a live<->edit flip and on a camera rig appearing, leaving
     *  the listener on an object nobody dispatches on. Both failures are silent.
     *  Every consumer already ticks this per frame, so the subscription could only
     *  ever duplicate a relift it had no epsilon to suppress. */
    syncCamera(): void {
      if (disposed || !enabled || !isActive()) return
      const view = cameraView(camera)
      if (passView && !viewMoved(passView, view)) return
      void relift()
    },

    /** Diagnostics: a copy of what the last pass wrote, for a test to decode and check. */
    packedWords: () => pass.packedWords(),

    /** Diagnostics, built fresh per read: fine for a 100 ms poll, wrong per frame. */
    get state() {
      const poses = { left: controller.pose('left'), right: controller.pose('right') }
      return {
        count: splatMesh.count,
        counts: { left: charts.left.numTexels, right: charts.right.numTexels },
        capacity: splatMesh.capacity,
        passes: pass.passes,
        provider: decoders.provider,
        enabled,
        visible,
        uvResolution: meta.uvResolution,
        colorChannels: pass.colorChannels,
        lookAtCamera: followCamera,
        maxGazeDegrees: controller.gaze.limits.coneDegrees,
        specGain,
        condOwners: owners,
        lastError,
        liftSubmitMs: lifter.lastSubmitMs,
        liftReadbackMs: lifter.lastReadbackMs,
        targets: {
          left: [...poses.left.targetDirection],
          right: [...poses.right.targetDirection],
        },
        aim: { left: poses.left.aim, right: poses.right.aim },
        poleTravelMm: { left: poses.left.poleTravelMm, right: poses.right.poleTravelMm },
        lightSource,
        lights: Array.from({ length: lights.count }, (_, index) => ({
          dir: [lights.directions[index * 3], lights.directions[index * 3 + 1],
            lights.directions[index * 3 + 2]],
          rgb: [lights.colors[index * 3], lights.colors[index * 3 + 1], lights.colors[index * 3 + 2]],
        })),
      }
    },

    dispose() {
      disposed = true
      void decoders.dispose()
      lifter.dispose()
      splatMesh.dispose()
      options.onDispose?.()
    },
  }
}

// No CPU fallback: a second lift is a second numeric answer to the same question.
async function buildLifter(assets: EyeAssets, capacity: number): Promise<GpuEyeLifter> {
  const device = await getOgsComputeDevice(EYE_LIFT_STORAGE_BUFFERS, 'the eye polar lift')
  if (!device) throw new Error('eyes: no WebGPU adapter for the polar lift')
  return new GpuEyeLifter(device, assets.charts, assets.meta, {
    left: assets.mesh.eyes.left.count,
    right: assets.mesh.eyes.right.count,
  }, capacity)
}
